/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import { API_ENDPOINT, DONOR_AUTH, ADMIN_AUTH, ORG_AUTH } from "./config";
import { checkLoginStatus } from "./assets/helper.js";

/**
  Parse query to prepare for request
  @param {JSON} query - The query being parsed
  @return {String} - The resulting string form of the query
*/
const queryParsing = (query) => {
  const keys = Object.keys(query);
  if (keys.length === 0) return "";
  if (keys.length === 1) {
    const key = keys[0];
    return "?" + key + "=" + query[key];
  }
  const firstKey = keys.shift(); // Extract and remove first key
  let queryString = "?" + firstKey + "=" + query[firstKey];
  for (let key of keys) {
    queryString += "&" + key + "=" + query[key];
  }
  return queryString;
};

/**
  Wrapper for js fetch
  @param {String} path - The path without API Endpoint
  @param {String} method - HTTP request method
  @param {JSON|FormData} params - The request parameters
  @param {Boolean} isFormData - Whether params is a FormData object
  @param {JSON} config - Additional configuration for the request
  @return {Promise} - The wrapped promise that will update login status
*/
const simpleFetch = (path, method, params = {}, isFormData = false, config = {}) => {
  let fetchPromise;
  const requestConfig = {
    credentials: "include",
    method: method,
    headers: {}
  };
  let accessToken;
  try {
    accessToken = localStorage.getItem("accessToken");
  } catch (error) {
    // localStorage can throw (e.g. blocked/partitioned storage in some
    // embedded webviews) - treat as no token rather than crashing the caller.
    accessToken = null;
  }
  
  if (config.isCustomDomain) {
    requestConfig.headers["x-checkplus-external"] = "true";
  }

  if (accessToken) {
    requestConfig.headers["x-checkplus-external"] = "true";
    requestConfig.headers["Authorization"] = "Bearer " + accessToken;
  }

  if (method === "GET") {
    requestConfig.headers["Content-Type"] = "application/json";
    requestConfig.headers["Accept"] = "application/json";
    requestConfig.cache = "no-cache";
    fetchPromise = fetch(API_ENDPOINT + path + queryParsing(params), requestConfig);
  } else if (isFormData) {
    // Handle FormData objects differently - don't set Content-Type as the browser will set it with the boundary
    requestConfig.body = params;
    fetchPromise = fetch(API_ENDPOINT + path, requestConfig);
  } else {
    requestConfig.headers["Content-Type"] = "application/json";
    requestConfig.headers["Accept"] = "application/json";
    requestConfig.body = JSON.stringify(params);
    fetchPromise = fetch(API_ENDPOINT + path, requestConfig);
  }

  return fetchPromise.then((res) => {
    if (res.status === 401) {
      const object = { status: false, timestamp: new Date().getTime() };
      let loggedIn = false;
      if (
        checkLoginStatus(ADMIN_AUTH) ||
        checkLoginStatus(ORG_AUTH) ||
        checkLoginStatus(DONOR_AUTH)
      ) {
        loggedIn = true;
      }

      localStorage.setItem(DONOR_AUTH, JSON.stringify(object));
      localStorage.setItem(ADMIN_AUTH, JSON.stringify(object));
      localStorage.setItem(ORG_AUTH, JSON.stringify(object));

      if (loggedIn) window.location.reload();

      return res
        .json()
        .then((body) => {
          return {
            status: res.status,
            body: body,
          };
        })
        .catch((err) => {
          return res.text().then((text) => {
            return {
              status: res.status,
              body: text || `HTTP ${res.status} Error`,
            };
          }).catch(() => {
            return {
              status: res.status,
              body: `HTTP ${res.status} Error - Unable to read response`,
            };
          });
        });
    }

    if (res.status !== 204) {
      return res
        .json()
        .then((body) => {
          return {
            status: res.status,
            body: body,
          };
        })
        .catch((err) => {
          // If JSON parsing fails, try to get text instead
          return res.text().then((text) => {
            return {
              status: res.status,
              body: text || `HTTP ${res.status} Error`,
            };
          }).catch(() => {
            return {
              status: res.status,
              body: `HTTP ${res.status} Error - Unable to read response`,
            };
          });
        });
    }
    return res;
  }).catch((err) => {
    return err;
  });
};

export { simpleFetch };